// Sales Bridge — read-only summary of the existing sales pipeline + intake.
// All editing flows live in the existing Sales Pipeline and Intake Parser
// pages — we deep-link instead of duplicating forms.
import { useEffect, useState } from "react";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Loader2, ExternalLink, FileText, ArrowRight } from "lucide-react";
import { fetchJson, fmtMoney, fmtDate, SectionHeader } from "./pccHelpers";

export default function PccSalesBridge() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await fetchJson("/deals");
        if (alive) setDeals(d || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Failed");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>;
  if (error) return <Card className="bg-red-500/[0.06] border-red-500/30 p-4 text-red-200">{error}</Card>;

  const active = deals.filter(d => !["closed_won", "closed_lost", "won", "lost"].includes((d.stage || "").toLowerCase()));
  const totalValue = active.reduce((s, d) => s + Number(d.value || 0), 0);

  return (
    <div className="space-y-5" data-testid="pcc-sales-bridge">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-light text-white">Sales Bridge</h2>
          <p className="text-white/55 text-sm">Pipeline overview. New intake + full pipeline editing happens in the existing modules.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm" className="border-white/15 bg-white/[0.04]"><a href="/intake-parser" target="_blank" rel="noreferrer"><FileText className="w-3.5 h-3.5 mr-1.5" />New Intake</a></Button>
          <Button asChild variant="outline" size="sm" className="border-white/15 bg-white/[0.04]"><a href="/sales-pipeline" target="_blank" rel="noreferrer">Open Pipeline <ExternalLink className="w-3.5 h-3.5 ml-1.5" /></a></Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-[#000000] border-white/[0.08] p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Open Deals</div>
          <div className="text-2xl font-light text-white mt-1">{active.length}</div>
        </Card>
        <Card className="bg-[#000000] border-white/[0.08] p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Pipeline Value</div>
          <div className="text-2xl font-light text-blue-300 tabular-nums mt-1">{fmtMoney(totalValue)}</div>
        </Card>
        <Card className="bg-[#000000] border-white/[0.08] p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Avg Deal Size</div>
          <div className="text-2xl font-light text-white tabular-nums mt-1">{fmtMoney(active.length ? totalValue / active.length : 0)}</div>
        </Card>
      </div>

      <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden">
        <SectionHeader dot="#8FA79B" title={`Active Pipeline (${active.length})`} />
        <div className="divide-y divide-white/[0.06] max-h-[600px] overflow-y-auto">
          {active.length === 0 ? (
            <div className="px-6 py-10 text-center text-white/40 text-sm">No open deals.</div>
          ) : active.map(d => (
            <div key={d.id} className="px-6 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium truncate">{d.title || d.name || d.client_name}</div>
                <div className="text-[11px] text-white/45 font-mono truncate">{d.client_name || "—"} · {d.owner_name || d.owner || "—"}</div>
              </div>
              <Badge className="bg-blue-500/15 text-blue-300 border border-blue-500/30">{d.stage || "—"}</Badge>
              <div className="text-sm font-mono tabular-nums w-28 text-right">{fmtMoney(d.value)}</div>
              <Button asChild variant="ghost" size="sm" className="text-amber-300 hover:text-amber-200">
                <a href={`/sales-pipeline?deal=${d.id}`} target="_blank" rel="noreferrer">Open <ArrowRight className="w-3.5 h-3.5 ml-1" /></a>
              </Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
